import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { lazy, Suspense } from "react";
import { Navigate, Outlet, Route, Routes } from "react-router-dom";
import { Toaster } from "sonner";
import { AppShell } from "@/components/layout/app-shell";
import { AppLoading } from "@/components/shared/loading";
import { AuthProvider, useAuth } from "@/contexts/auth-context";
import type { Role } from "@/types";

const AuditPage = lazy(() =>
  import("@/features/admin/audit-page").then((module) => ({
    default: module.AuditPage,
  })),
);
const CommunicationsPage = lazy(() =>
  import("@/features/admin/communications-page").then((module) => ({
    default: module.CommunicationsPage,
  })),
);
const CourseDetailPage = lazy(() =>
  import("@/features/admin/course-detail-page").then((module) => ({
    default: module.CourseDetailPage,
  })),
);
const CoursesPage = lazy(() =>
  import("@/features/admin/courses-page").then((module) => ({
    default: module.CoursesPage,
  })),
);
const DocumentsPage = lazy(() =>
  import("@/features/admin/documents-page").then((module) => ({
    default: module.DocumentsPage,
  })),
);
const ExamsPage = lazy(() =>
  import("@/features/admin/exams-page").then((module) => ({
    default: module.ExamsPage,
  })),
);
const OrganizationPage = lazy(() =>
  import("@/features/admin/organization-page").then((module) => ({
    default: module.OrganizationPage,
  })),
);
const QuestionsPage = lazy(() =>
  import("@/features/admin/questions-page").then((module) => ({
    default: module.QuestionsPage,
  })),
);
const ReportsPage = lazy(() =>
  import("@/features/admin/reports-page").then((module) => ({
    default: module.ReportsPage,
  })),
);
const UsersPage = lazy(() =>
  import("@/features/admin/users-page").then((module) => ({
    default: module.UsersPage,
  })),
);
const ForgotPasswordPage = lazy(() =>
  import("@/features/auth/forgot-password-page").then((module) => ({
    default: module.ForgotPasswordPage,
  })),
);
const LoginPage = lazy(() =>
  import("@/features/auth/login-page").then((module) => ({
    default: module.LoginPage,
  })),
);
const ProfilePage = lazy(() =>
  import("@/features/auth/profile-page").then((module) => ({
    default: module.ProfilePage,
  })),
);
const ResetPasswordPage = lazy(() =>
  import("@/features/auth/reset-password-page").then((module) => ({
    default: module.ResetPasswordPage,
  })),
);
const CertificatesPage = lazy(() =>
  import("@/features/certificates/certificates-page").then((module) => ({
    default: module.CertificatesPage,
  })),
);
const VerifyCertificatePage = lazy(() =>
  import("@/features/certificates/verify-certificate-page").then((module) => ({
    default: module.VerifyCertificatePage,
  })),
);
const DashboardPage = lazy(() =>
  import("@/features/dashboard/dashboard-page").then((module) => ({
    default: module.DashboardPage,
  })),
);
const CatalogPage = lazy(() =>
  import("@/features/learning/catalog-page").then((module) => ({
    default: module.CatalogPage,
  })),
);
const CatalogDetailPage = lazy(() =>
  import("@/features/learning/catalog-detail-page").then((module) => ({
    default: module.CatalogDetailPage,
  })),
);
const ExamPage = lazy(() =>
  import("@/features/learning/exam-page").then((module) => ({
    default: module.ExamPage,
  })),
);
const ExamResultsPage = lazy(() =>
  import("@/features/learning/exam-page").then((module) => ({
    default: module.ExamResultsPage,
  })),
);
const ResultsPage = lazy(() =>
  import("@/features/learning/exam-page").then((module) => ({
    default: module.ResultsPage,
  })),
);
const LearningPage = lazy(() =>
  import("@/features/learning/learning-page").then((module) => ({
    default: module.LearningPage,
  })),
);
const MyCoursesPage = lazy(() =>
  import("@/features/learning/my-courses-page").then((module) => ({
    default: module.MyCoursesPage,
  })),
);
const MyDocumentsPage = lazy(() =>
  import("@/features/learning/my-documents-page").then((module) => ({
    default: module.MyDocumentsPage,
  })),
);
const NotificationsPage = lazy(() =>
  import("@/features/notifications/notifications-page").then((module) => ({
    default: module.NotificationsPage,
  })),
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 20_000, retry: 1, refetchOnWindowFocus: false },
    mutations: { retry: 0 },
  },
});

function ProtectedRoute({ roles }: { roles?: Role[] }) {
  const { user, loading } = useAuth();
  if (loading) return <AppLoading />;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role))
    return <Navigate to="/dashboard" replace />;
  return <Outlet />;
}

function NotFoundPage() {
  const { user } = useAuth();
  return (
    <div
      className="d-flex flex-column align-items-center justify-content-center text-center p-24pt"
      style={{ minHeight: "100vh" }}
    >
      <h1 className="display-3 text-primary mb-0">404</h1>
      <h2 className="mb-8pt">Pagina no encontrada</h2>
      <p className="text-50 measure-paragraph">
        La direccion solicitada no existe o ya no esta disponible.
      </p>
      <a className="btn btn-primary" href={user ? "/dashboard" : "/login"}>
        Volver al inicio
      </a>
    </div>
  );
}

const managementRoles: Role[] = [
  "ADMIN",
  "TRAINING_MANAGER",
  "INSTRUCTOR",
  "AUDITOR",
];
const administrationRoles: Role[] = ["ADMIN", "TRAINING_MANAGER", "AUDITOR"];

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Suspense fallback={<AppLoading />}>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/verify/:code" element={<VerifyCertificatePage />} />

            <Route element={<ProtectedRoute />}>
              <Route element={<AppShell />}>
                <Route index element={<Navigate to="/dashboard" replace />} />
                <Route path="dashboard" element={<DashboardPage />} />
                <Route path="profile" element={<ProfilePage />} />
                <Route path="notifications" element={<NotificationsPage />} />

                <Route element={<ProtectedRoute roles={["PARTICIPANT"]} />}>
                  <Route path="my-courses" element={<MyCoursesPage />} />
                  <Route path="catalog" element={<CatalogPage />} />
                  <Route
                    path="catalog/:courseId"
                    element={<CatalogDetailPage />}
                  />
                  <Route path="learn/:courseId" element={<LearningPage />} />
                  <Route path="exam/:examId" element={<ExamPage />} />
                  <Route path="results" element={<ResultsPage />} />
                  <Route
                    path="results/:attemptId"
                    element={<ExamResultsPage />}
                  />
                  <Route path="my-documents" element={<MyDocumentsPage />} />
                  <Route path="certificates" element={<CertificatesPage />} />
                </Route>

                <Route element={<ProtectedRoute roles={managementRoles} />}>
                  <Route path="admin/courses" element={<CoursesPage />} />
                  <Route
                    path="admin/courses/:courseId"
                    element={<CourseDetailPage />}
                  />
                  <Route path="admin/questions" element={<QuestionsPage />} />
                  <Route path="admin/exams" element={<ExamsPage />} />
                  <Route path="admin/reports" element={<ReportsPage />} />
                </Route>

                <Route element={<ProtectedRoute roles={administrationRoles} />}>
                  <Route path="admin/users" element={<UsersPage />} />
                  <Route
                    path="admin/organization"
                    element={<OrganizationPage />}
                  />
                  <Route path="admin/documents" element={<DocumentsPage />} />
                  <Route
                    path="admin/communications"
                    element={<CommunicationsPage />}
                  />
                  <Route path="admin/audit" element={<AuditPage />} />
                </Route>
              </Route>
            </Route>
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
        <Toaster position="top-right" richColors closeButton />
      </AuthProvider>
    </QueryClientProvider>
  );
}
